import React, { useState, useMemo } from 'react';
import {
  Search, ChevronDown, ChevronRight, Shield, Globe, Zap,
  AlertTriangle, CheckCircle, Copy, Check, Terminal, Key,
  Layers, FileJson, Lightbulb,
} from 'lucide-react';
import {
  EVENT_SPECS, ENVELOPE_FIELDS, AUTH_INFO, ERROR_CODES, BEST_PRACTICES,
  type EventSpec, type PayloadField,
} from '../data/pam-integration';
import { DOMAIN_COLORS } from '../data/schema';

type Tab = 'overview' | 'auth' | 'envelope' | 'events' | 'errors' | 'bestpractices';
type DomainFilter = 'all' | 'casino' | 'sport' | 'finance' | 'player';

const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'overview',      label: 'Overview',        icon: Globe },
  { key: 'auth',          label: 'Authentication',  icon: Key },
  { key: 'envelope',      label: 'Envelope',        icon: FileJson },
  { key: 'events',        label: 'Event Types',     icon: Layers },
  { key: 'errors',        label: 'Error Handling',  icon: AlertTriangle },
  { key: 'bestpractices', label: 'Best Practices',  icon: Lightbulb },
];

const DOMAINS: DomainFilter[] = ['all', 'casino', 'sport', 'finance', 'player'];

function DomainBadge({ domain }: { domain: string }) {
  const colors = DOMAIN_COLORS[domain] || { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30' };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
      {domain}
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="absolute right-3 top-3 rounded p-1 text-slate-400 hover:text-white transition-colors" title="Copy">
      {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

function CodeBlock({ code, language = 'json' }: { code: string; language?: string }) {
  return (
    <div className="relative mt-3 rounded-lg bg-slate-900 dark:bg-slate-950 border border-slate-700">
      <div className="flex items-center justify-between border-b border-slate-700 px-4 py-2">
        <span className="text-xs font-medium text-slate-400">{language.toUpperCase()}</span>
      </div>
      <CopyButton text={code} />
      <pre className="overflow-x-auto p-4 text-sm leading-relaxed text-slate-300">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function FieldTable({ fields }: { fields: PayloadField[] }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/80">
          <tr>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Field</th>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Type</th>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 w-16">Req.</th>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {fields.map((f) => (
            <tr key={f.name} className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td className="px-4 py-2 font-mono text-xs text-accent dark:text-accent-300">{f.name}</td>
              <td className="px-4 py-2 font-mono text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">{f.type}</td>
              <td className="px-4 py-2 text-center">
                {f.required
                  ? <CheckCircle className="inline h-3.5 w-3.5 text-green-500" />
                  : <span className="text-xs text-slate-400">-</span>
                }
              </td>
              <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                {f.description}
                {f.enum && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {f.enum.map((v) => (
                      <code key={v} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700 dark:bg-slate-700 dark:text-slate-300">{v}</code>
                    ))}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventCard({ event }: { event: EventSpec }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600">
      <button onClick={() => setExpanded(!expanded)} className="flex w-full items-start justify-between text-left">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <Zap className="h-4 w-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{event.name}</span>
            <DomainBadge domain={event.domain} />
          </div>
          <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{event.description}</p>
        </div>
        <div className="ml-4 flex items-center gap-2 flex-shrink-0">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
            {event.fields.filter((f) => f.required).length}/{event.fields.length} req
          </span>
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div>
          <FieldTable fields={event.fields} />
          <div className="mt-3">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Example payload</p>
            <CodeBlock code={JSON.stringify(event.example, null, 2)} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Full envelope example for the Overview tab ──
const ENVELOPE_EXAMPLE = `{
  "event_id": "550e8400-e29b-41d4-a716-446655440000",
  "event_date": "2026-03-20T14:30:00Z",
  "site_id": 1,
  "player_id": "player-001",
  "event_type": "casino_bet",
  "domain": "casino",
  "payload": {
    "round_id": "r-abc-123",
    "transaction_id": 10001,
    "brand": "pragmatic",
    "game": "sweet-bonanza",
    "game_type": "slots",
    "amount_real": 8.50,
    "amount_bonus": 1.50,
    "currency": "EUR"
  },
  "produced_at": "2026-03-20T14:30:01Z",
  "producer_app": "my-pam-platform",
  "schema_version": "v1"
}`;

const CURL_EXAMPLE = `curl -X POST http://ingestor:8080/v1/events \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: sk_live_a1b2c3d4e5f6..." \\
  -d '{
    "event_id": "550e8400-e29b-41d4-a716-446655440000",
    "event_date": "2026-03-20T14:30:00Z",
    "site_id": 1,
    "player_id": "player-001",
    "event_type": "casino_bet",
    "domain": "casino",
    "payload": {
      "round_id": "r-abc-123",
      "transaction_id": 10001,
      "brand": "pragmatic",
      "game": "sweet-bonanza",
      "amount_real": 8.50,
      "amount_bonus": 1.50,
      "currency": "EUR"
    },
    "produced_at": "2026-03-20T14:30:01Z",
    "producer_app": "my-pam",
    "schema_version": "v1"
  }'`;

const GRPC_PROTO = `service EventService {
  // Single event
  rpc IngestEvent(IngestEventRequest) returns (IngestEventResponse);

  // Batch stream (high throughput)
  rpc IngestStream(stream IngestEventRequest) returns (IngestStreamResponse);
}

message IngestEventRequest {
  string event_id     = 1;
  string event_type   = 2;
  string domain       = 3;
  int64  site_id      = 4;
  int64  player_id    = 5;
  string event_date   = 6;  // ISO 8601
  string country_code = 7;  // ISO 3166-1 alpha-2
  string currency     = 8;  // ISO 4217
  string payload_json = 9;  // Event payload as JSON string
}`;

const ERROR_EXAMPLE = `{
  "error": {
    "code": "validation_error",
    "message": "Envelope validation failed",
    "details": {
      "event_type": "unknown event type: casino_unknown"
    }
  }
}`;

export const PamIntegration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');

  const filteredEvents = useMemo(() => {
    return EVENT_SPECS.filter((e) => {
      if (domainFilter !== 'all' && e.domain !== domainFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return e.name.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.fields.some((f) => f.name.toLowerCase().includes(q));
      }
      return true;
    });
  }, [search, domainFilter]);

  const eventCounts = {
    casino: EVENT_SPECS.filter((e) => e.domain === 'casino').length,
    sport: EVENT_SPECS.filter((e) => e.domain === 'sport').length,
    finance: EVENT_SPECS.filter((e) => e.domain === 'finance').length,
    player: EVENT_SPECS.filter((e) => e.domain === 'player').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">PAM Integration Guide</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          How to integrate your Player Account Management system with bs-reporting
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Event Types', value: EVENT_SPECS.length, icon: Zap },
          { label: 'Domains', value: 4, icon: Layers },
          { label: 'Protocols', value: '2 (HTTP + gRPC)', icon: Globe },
          { label: 'Auth', value: 'API Key', icon: Shield },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-2">
              <s.icon className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <span className="text-sm text-slate-500 dark:text-slate-400">{s.label}</span>
            </div>
            <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/50">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-slate-600 hover:bg-white hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Overview ── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Architecture */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Architecture</h2>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900 p-4 font-mono text-xs leading-6 text-slate-700 dark:text-slate-300 overflow-x-auto">
              <pre>{`PAM System
    │
    │  POST /v1/events  (HTTP)
    │  IngestEvent       (gRPC)
    │  IngestStream      (gRPC batch)
    ▼
┌──────────────┐     ┌──────────────┐     ┌──────────────┐
│   Ingestor   │────▶│   Redpanda   │────▶│  ClickHouse  │
│  validate +  │     │  bs.casino   │     │  Bronze ──▶  │
│  authorize   │     │  bs.sport    │     │  Gold tables │
│              │     │  bs.finance  │     │  (KPI agg)   │
└──────────────┘     │  bs.player   │     └──────────────┘
                     └──────────────┘`}</pre>
            </div>
          </div>

          {/* Quick Start */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Quick Start</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Send a JSON event to the ingestor HTTP endpoint with your API key:
            </p>
            <CodeBlock code={CURL_EXAMPLE} language="bash" />
            <div className="mt-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/50 p-3">
              <p className="text-sm text-green-800 dark:text-green-300">
                <strong>Success response (202):</strong>{' '}
                <code className="rounded bg-green-100 dark:bg-green-900/40 px-1.5 py-0.5">
                  {`{"event_id":"...","status":"queued"}`}
                </code>
              </p>
            </div>
          </div>

          {/* Domain summary */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Event Domains</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {([
                { domain: 'casino', desc: 'Bet, win, and refund events for casino games (slots, table, live, crash)', count: eventCounts.casino },
                { domain: 'sport', desc: 'Sportsbook bets, wins, cancellations, cashouts, and rollbacks', count: eventCounts.sport },
                { domain: 'finance', desc: 'Deposits, withdrawals, bonuses, and cashback payouts', count: eventCounts.finance },
                { domain: 'player', desc: 'Registration, login, status changes, KYC, and email verification', count: eventCounts.player },
              ] as const).map((d) => (
                <div key={d.domain} className="rounded-lg border border-slate-100 dark:border-slate-700 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DomainBadge domain={d.domain} />
                    <span className="text-xs text-slate-400">{d.count} events</span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* gRPC */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">gRPC API</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              For high-throughput ingestion, use the gRPC API. <code className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-xs">IngestStream</code> accepts a stream of events in a single call — ideal for batch imports.
            </p>
            <CodeBlock code={GRPC_PROTO} language="protobuf" />
          </div>
        </div>
      )}

      {/* ── Authentication ── */}
      {activeTab === 'auth' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">API Key Authentication</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              All requests must include an API key in the <code className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-xs">X-API-Key</code> header.
            </p>
            <CodeBlock code={`X-API-Key: sk_live_a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2`} language="http" />

            <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-left text-sm">
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {([
                    ['Header', AUTH_INFO.header],
                    ['Key Format', AUTH_INFO.format],
                    ['Hash Algorithm', AUTH_INFO.hash],
                    ['Cache TTL', AUTH_INFO.cacheTTL],
                  ] as const).map(([label, value]) => (
                    <tr key={label} className="bg-white dark:bg-slate-900">
                      <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300 w-40">{label}</td>
                      <td className="px-4 py-3 font-mono text-sm text-slate-900 dark:text-white">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Site Authorization</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              Each API key is authorized for specific <code className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-xs">site_id</code> values.
              Sending an event for an unauthorized site returns <strong>403 Forbidden</strong>.
            </p>
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/50 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                A single API key can be authorized for multiple sites. The ingestor checks site_id on every request.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Envelope ── */}
      {activeTab === 'envelope' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Event Envelope</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Every event is wrapped in a standard envelope. The HTTP body is the full envelope as JSON.
            </p>
            <FieldTable
              fields={ENVELOPE_FIELDS.map((f) => ({
                name: f.name,
                type: f.type,
                required: f.required,
                description: f.description,
              }))}
            />
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Full Example</h2>
            <CodeBlock code={ENVELOPE_EXAMPLE} />
          </div>

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 dark:border-accent-900/50 dark:bg-accent-900/20">
            <p className="text-sm text-accent-800 dark:text-accent-200">
              <strong>Notes:</strong> event_id is the idempotency key — duplicates are ignored.
              domain is auto-inferred from event_type and can be omitted.
              event_date must be within the last 48 hours.
              No additional properties are allowed (strict schema validation).
            </p>
          </div>
        </div>
      )}

      {/* ── Event Types ── */}
      {activeTab === 'events' && (
        <div className="space-y-4">
          {/* Search + filter */}
          <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <div className="relative flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Search</label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search events, fields..."
                  className="w-full rounded-md border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Domain</label>
              <div className="mt-1 flex flex-wrap gap-1">
                {DOMAINS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDomainFilter(d)}
                    className={`rounded-md px-3 py-2 text-xs font-medium capitalize transition-colors ${
                      domainFilter === d
                        ? 'bg-accent text-white'
                        : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Event cards */}
          <div className="space-y-3">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((e) => <EventCard key={e.name} event={e} />)
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
                <p className="text-sm text-slate-500">No events match your search.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Errors ── */}
      {activeTab === 'errors' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Error Response Format</h2>
            <CodeBlock code={ERROR_EXAMPLE} />
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Error Codes</h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/80">
                  <tr>
                    <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</th>
                    <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Code</th>
                    <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {ERROR_CODES.map((e) => (
                    <tr key={e.code} className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-4 py-2">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          e.status >= 500 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          e.status >= 400 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        }`}>
                          {e.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-900 dark:text-white">{e.code}</td>
                      <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">{e.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Validation Rules</h2>
            <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              {[
                'event_id must be a valid UUID (v1-v7)',
                'event_date must be within the last 48 hours',
                'site_id must be >= 1 and authorized for the API key',
                'player_id must be non-empty',
                'event_type must be one of the 19 recognized types',
                'payload must conform to the JSON Schema for the given event_type',
                'No additional properties allowed in envelope or payload',
              ].map((r) => (
                <li key={r} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── Best Practices ── */}
      {activeTab === 'bestpractices' && (
        <div className="space-y-4">
          {BEST_PRACTICES.map((bp) => (
            <div key={bp.title} className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
              <h3 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white mb-3">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                {bp.title}
              </h3>
              <ul className="space-y-2">
                {bp.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Terminal className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <p className="text-center text-xs text-slate-400 dark:text-slate-600">
        {EVENT_SPECS.length} event types &middot; 4 domains &middot; HTTP + gRPC
      </p>
    </div>
  );
};
